import { followUser, getIsFollowing, getUserProfile, unfollowUser, getFollowerCount, getUserReviews, getServicesByHost } from "apis";
import { PageHeader, UserPostWrapper } from "components";
import ReviewSection from "./ReviewSection";
import ReportModal from "components/common/ReportModal";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { UserDTO } from "types";
import { ServiceDTO } from "types/DTOs/service/service.dto";
import { useUserStore } from "store";
import { getImageUrl } from "utils/image.util";

interface UserProfileComponentProps {
    profileId: string;
}

const UserProfileComponent = ({ profileId }: UserProfileComponentProps) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const { user: currentUser } = useUserStore();

    const [user, setUser] = useState<UserDTO | null>(null);
    const [isFollowing, setIsFollowing] = useState<boolean>(false);
    const [isButtonDisabled, setIsButtonDisabled] = useState(false);
    const [followerCount, setFollowerCount] = useState(0);
    const [avgRating, setAvgRating] = useState(0);
    const [reviewCount, setReviewCount] = useState(0);
    const [activeTab, setActiveTab] = useState<"posts" | "reviews" | "services">("services");
    const [hostServices, setHostServices] = useState<ServiceDTO[]>([]);
    const [showReport, setShowReport] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        if (profileId) {
            getUserData();
        }
    }, [profileId]);

    const getUserData = async () => {
        if (profileId) {
            const response = await getUserProfile(profileId);
            const userData = response.data.data;
            setUser(userData);

            try {
                const isFollowingResponse = await getIsFollowing(profileId);
                setIsFollowing(isFollowingResponse.data.data);
            } catch (e) {}

            // Load social stats
            try {
                const countRes = await getFollowerCount(userData.id);
                setFollowerCount(countRes.data.data || 0);
            } catch (e) {}

            try {
                const reviewRes = await getUserReviews(userData.id);
                setAvgRating(reviewRes.data.data.averageRating || 0);
                setReviewCount(reviewRes.data.data.totalCount || 0);
            } catch (e) {}

            // Load host services
            try {
                const servicesRes = await getServicesByHost(userData.id);
                setHostServices(servicesRes.data.data || []);
            } catch (e) {}
        }
    }

    const handleClick = async () => {
        if (isButtonDisabled) return;
        setIsButtonDisabled(true);

        try {
            if (!isFollowing) {
                const response = await followUser(profileId);
                if (response.data.isSuccess === "T") {
                    setIsFollowing(true);
                    setFollowerCount(prev => prev + 1);
                }
            } else {
                const response = await unfollowUser(profileId);
                if (response.data.isSuccess === "T") {
                    setIsFollowing(false);
                    setFollowerCount(prev => Math.max(0, prev - 1));
                }
            }
        } catch (e) {
            console.error("팔로우 처리 중 오류", e);
        } finally {
            setTimeout(() => setIsButtonDisabled(false), 1000);
        }
    };

    return (
        <div className="flex flex-col animate-fadeUp">
            <PageHeader title={profileId} isArrow />

            {/* Banner */}
            <div className="relative">
                {user?.bannerImage ? (
                    <img
                        src={getImageUrl(user.bannerImage)}
                        alt="banner"
                        className="w-full h-[200px] object-cover"
                    />
                ) : (
                    <div className="w-full h-[140px] bg-gradient-to-br from-primary-100 to-violet-100" />
                )}
            </div>

            {/* Profile info */}
            <div className="bg-white rounded-2xl mobile:rounded-none -mt-6 relative z-10 mx-4 mobile:mx-0">
                <div className="p-6">
                    <div className="flex justify-between items-start">
                        <div className="flex gap-3 -mt-12">
                            <div className="w-20 h-20 rounded-full border-4 border-white overflow-hidden bg-achromatic-100 shadow-sm">
                                {user?.profileImage ? (
                                    <img src={getImageUrl(user.profileImage)}
                                        alt="profile" className="w-full h-full object-cover" />
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center text-2xl text-achromatic-300">👤</div>
                                )}
                            </div>
                        </div>
                        <div className="flex gap-2" ref={containerRef}>
                            {/* Share button */}
                            <button
                                onClick={() => {
                                    navigator.clipboard.writeText(`${window.location.origin}/profile/@${profileId}`);
                                    alert('프로필 링크가 복사되었습니다!');
                                }}
                                className="w-9 h-9 flex items-center justify-center bg-achromatic-50 rounded-xl text-achromatic-500 hover:bg-achromatic-100 transition-all text-sm"
                                title="프로필 공유"
                            >
                                🔗
                            </button>
                            {currentUser?.profileId !== profileId && (
                                <>
                                    <button
                                        onClick={() => setShowReport(true)}
                                        className="w-9 h-9 flex items-center justify-center bg-achromatic-50 rounded-xl text-achromatic-500 hover:bg-red-50 hover:text-red-500 transition-all text-sm"
                                        title="신고하기"
                                    >
                                        ⚠️
                                    </button>
                                    <button
                                        ref={buttonRef}
                                        disabled={isButtonDisabled}
                                        className={`transition-all duration-200 rounded-xl px-5 py-2 text-[12px] font-bold
                                            ${isFollowing
                                                ? "bg-achromatic-900 text-white hover:bg-achromatic-800"
                                                : "bg-primary-500 text-white hover:bg-primary-600"
                                            }
                                            ${isButtonDisabled ? 'opacity-60 cursor-not-allowed' : ''}
                                            active:scale-95`}
                                        onClick={handleClick}
                                    >
                                        {isFollowing ? '✓ 즐겨찾기' : '⭐ 즐겨찾기'}
                                    </button>
                                </>
                            )}
                        </div>
                    </div>

                    {/* Name and ID */}
                    <div className="mt-3">
                        <div className="text-lg font-extrabold text-achromatic-900">{user?.name}</div>
                        <div className="text-[13px] text-achromatic-400">@{user?.profileId}</div>
                    </div>

                    {/* Social Stats — 당근 스타일 */}
                    <div className="flex gap-6 mt-4 pt-4 border-t border-achromatic-100">
                        <div className="flex flex-col items-center">
                            <span className="text-lg font-extrabold text-achromatic-900">
                                {avgRating > 0 ? avgRating : '-'}
                            </span>
                            <span className="text-[11px] text-achromatic-400">
                                {avgRating > 0 ? '⭐ 평점' : '⭐ 평점'}
                            </span>
                        </div>
                        <div className="flex flex-col items-center">
                            <span className="text-lg font-extrabold text-achromatic-900">{reviewCount}</span>
                            <span className="text-[11px] text-achromatic-400">📋 거래</span>
                        </div>
                        <div className="flex flex-col items-center">
                            <span className="text-lg font-extrabold text-achromatic-900">{followerCount}</span>
                            <span className="text-[11px] text-achromatic-400">❤️ 즐겨찾기</span>
                        </div>
                    </div>
                </div>

                {/* Tabs */}
                <div className="flex border-t border-achromatic-100">
                    <button onClick={() => setActiveTab("services")}
                        className={`flex-1 py-3 text-[13px] font-bold text-center transition-colors
                            ${activeTab === "services"
                                ? "text-achromatic-900 border-b-2 border-achromatic-900"
                                : "text-achromatic-400 hover:text-achromatic-600"}`}>
                        서비스 ({hostServices.length})
                    </button>
                    <button onClick={() => setActiveTab("posts")}
                        className={`flex-1 py-3 text-[13px] font-bold text-center transition-colors
                            ${activeTab === "posts"
                                ? "text-achromatic-900 border-b-2 border-achromatic-900"
                                : "text-achromatic-400 hover:text-achromatic-600"}`}>
                        게시물
                    </button>
                    <button onClick={() => setActiveTab("reviews")}
                        className={`flex-1 py-3 text-[13px] font-bold text-center transition-colors
                            ${activeTab === "reviews"
                                ? "text-achromatic-900 border-b-2 border-achromatic-900"
                                : "text-achromatic-400 hover:text-achromatic-600"}`}>
                        리뷰 ({reviewCount})
                    </button>
                </div>
            </div>

            {/* Content */}
            <div className="mt-4 mx-4 mobile:mx-0">
                {activeTab === "services" && (
                    <div className="bg-white rounded-2xl overflow-hidden mobile:rounded-none">
                        {hostServices.length === 0 ? (
                            <div className="flex flex-col items-center gap-2 py-12 text-achromatic-300">
                                <span className="text-3xl">📦</span>
                                <span className="text-sm">등록된 서비스가 없습니다</span>
                            </div>
                        ) : (
                            <div className="flex flex-col">
                                {hostServices.map(svc => (
                                    <div key={svc.id}
                                        onClick={() => navigate(`/service/${svc.id}`)}
                                        className="flex items-center gap-3 p-4 border-b border-achromatic-50 last:border-b-0 cursor-pointer hover:bg-achromatic-50 transition-colors">
                                        {svc.images && svc.images.length > 0 ? (
                                            <img
                                                src={getImageUrl(svc.images[0])}
                                                alt={svc.title}
                                                className="w-16 h-16 rounded-xl object-cover flex-shrink-0"
                                            />
                                        ) : (
                                            <div className="w-16 h-16 rounded-xl bg-achromatic-100 flex items-center justify-center text-xl flex-shrink-0">📦</div>
                                        )}
                                        <div className="flex-1 min-w-0">
                                            <div className="text-sm font-bold text-achromatic-900 truncate">{svc.title}</div>
                                            <div className="text-xs text-achromatic-400 mt-0.5">
                                                {svc.category} · {svc.durationMinutes}분
                                            </div>
                                            <div className="text-sm font-bold text-primary-500 mt-1">
                                                {svc.price.toLocaleString()}원
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
                {activeTab === "posts" && user && (
                    <div className="bg-white rounded-2xl overflow-hidden mobile:rounded-none">
                        <UserPostWrapper user={user} />
                    </div>
                )}
                {activeTab === "reviews" && user && (
                    <div className="bg-white rounded-2xl overflow-hidden mobile:rounded-none p-6">
                        <ReviewSection userId={user.id} />
                    </div>
                )}
            </div>

            {/* Report Modal */}
            {user && (
                <ReportModal
                    isOpen={showReport}
                    onClose={() => setShowReport(false)}
                    targetType="user"
                    targetId={user.id}
                    targetName={user.name}
                />
            )}
        </div>
    );
}

export default UserProfileComponent;